import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Terminal, CheckCircle2, XCircle } from 'lucide-react'

export default function DeviceAuthPage() {
  const { isAuthenticated, loginWithGitHub } = useAuth()
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  async function handleAuthorize() {
    const trimmed = code.trim().toUpperCase()
    if (!trimmed) return

    setStatus('loading')
    setErrorMessage('')

    const response = await apiClient.authorizeDevice(trimmed)
    if (response.success) {
      setStatus('success')
    } else {
      setStatus('error')
      setErrorMessage(response.error || 'Invalid or expired code. Please check and try again.')
    }
  }

  if (!isAuthenticated) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Terminal className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Authorize CLI</CardTitle>
            <CardDescription>
              Sign in to authorize the EtherPilot CLI on your device.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => loginWithGitHub()} className="w-full" size="lg">
              Sign in with GitHub
            </Button>
          </CardContent>
        </Card>
      </main>
    )
  }

  if (status === 'success') {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            </div>
            <CardTitle>Device Authorized</CardTitle>
            <CardDescription>
              You can close this window and return to the terminal.
            </CardDescription>
          </CardHeader>
        </Card>
      </main>
    )
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Terminal className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Authorize CLI</CardTitle>
          <CardDescription>
            Enter the code shown in your terminal to authorize the EtherPilot CLI.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="XXXX-0000"
            className="text-center text-2xl tracking-widest font-mono"
            maxLength={9}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleAuthorize()}
          />
          {status === 'error' && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <XCircle className="h-4 w-4" />
              {errorMessage}
            </div>
          )}
          <Button
            onClick={handleAuthorize}
            className="w-full"
            size="lg"
            disabled={code.trim().length < 9 || status === 'loading'}
          >
            {status === 'loading' ? 'Authorizing...' : 'Authorize'}
          </Button>
        </CardContent>
      </Card>
    </main>
  )
}
